import { useState } from 'react'
import { Building2, Check, ChevronsUpDown } from 'lucide-react'

import type { Company } from '@/types/api'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useCompanies,
  useEnsureActiveCompany,
} from '@/lib/queries/companies'
import { useCompanyStore } from '@/stores/company'
import { cn } from '@/lib/utils'

export function CompanySwitcher() {
  const [open, setOpen] = useState(false)
  // Garante que sempre exista uma company ativa (default: primeira da lista),
  // mesmo quando nenhuma página ainda chamou `useActiveCompanyId`.
  useEnsureActiveCompany()
  const { data: companies, isLoading } = useCompanies()
  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId)
  const setActiveCompany = useCompanyStore((s) => s.setActiveCompany)

  const active: Company | undefined = companies?.find(
    (c) => c.id === activeCompanyId,
  )

  if (isLoading) {
    return <Skeleton className="h-9 w-44" />
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-label="Trocar de company"
          className="h-9 w-full max-w-[260px] justify-between gap-2"
        >
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="truncate text-sm">
              {active?.name ?? 'Selecionar company…'}
            </span>
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar company…" />
          <CommandList>
            <CommandEmpty>Nenhuma company encontrada.</CommandEmpty>
            <CommandGroup>
              {companies?.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.name} ${c.id}`}
                  onSelect={() => {
                    setActiveCompany(c.id)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      c.id === activeCompanyId ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{c.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
